import { useCallback, useEffect, useState } from 'react';

import { DEFAULT_JSON_SCHEMA } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/constants';
import { JsonProperty } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/properties/types';
import { PropertyUtils } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/properties/utils';
import {
  EditingState,
  JsonSchema,
  JsonSchemaBuilderState,
  PropertyEditFormData
} from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/types';
import {
  SchemaUtils,
  ValidationUtils
} from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/utils';

export const useJsonSchemaBuilder = (initialSchema?: string) => {
  const [state, setState] = useState<JsonSchemaBuilderState>(() => {
    let schema = DEFAULT_JSON_SCHEMA;

    if (initialSchema) {
      try {
        const parsed = JSON.parse(initialSchema);
        if (parsed.type === 'object' && parsed.properties) {
          schema = parsed;
        }
      } catch {
        // Use default if parsing fails
      }
    }

    return {
      schema,
      editingState: { mode: 'none' }
    };
  });

  // Schema accessor functions - single source of truth
  const getSchema = useCallback(() => state.schema, [state.schema]);

  const getSchemaString = useCallback(() => SchemaUtils.generateJsonString(state.schema), [state.schema]);

  const getPropertyAtSchemaPath = useCallback(
    (path: string[]) => {
      return SchemaUtils.getPropertyAtPath(state.schema, path);
    },
    [state.schema]
  );

  const getExistingKeys = useCallback(
    (parentPath: string[]) => {
      if (parentPath.length === 0) {
        return Object.keys(state.schema.properties);
      }

      const parentProperty = SchemaUtils.getPropertyAtPath(state.schema, parentPath);
      if (parentProperty?.type === 'object' && parentProperty.properties) {
        return Object.keys(parentProperty.properties);
      }

      return [];
    },
    [state.schema]
  );

  const isPropertyRequired = useCallback(
    (fullPath: string[]) => {
      if (fullPath.length === 0) return false;

      const parentPath = fullPath.slice(0, -1);
      const key = fullPath[fullPath.length - 1];

      // Array items are always required
      if (key === 'items') return true;

      if (parentPath.length === 0) {
        return state.schema.required.includes(key);
      }

      const parentProperty = SchemaUtils.getPropertyAtPath(state.schema, parentPath);
      if (parentProperty?.type === 'object') {
        return parentProperty.required.includes(key);
      }

      return false;
    },
    [state.schema]
  );

  const updateSchema = useCallback((updater: (schema: JsonSchema) => JsonSchema) => {
    setState((prev) => ({
      ...prev,
      schema: updater(prev.schema)
    }));
  }, []);

  const setEditingState = useCallback((editingState: EditingState) => {
    setState((prev) => ({ ...prev, editingState }));
  }, []);

  const startAddingProperty = useCallback(
    (parentPath: string[] = []) => {
      if (!ValidationUtils.validateNestingLevel([...parentPath, 'newPropertyPlaceholder'])) {
        return false;
      }

      setEditingState({ mode: 'add', path: parentPath });
      return true;
    },
    [setEditingState]
  );

  const startEditingProperty = useCallback(
    (fullPath: string[]) => {
      setEditingState({ mode: 'edit', path: fullPath });
    },
    [setEditingState]
  );

  const cancelEditing = useCallback(() => {
    setEditingState({ mode: 'none' });
  }, [setEditingState]);

  const saveProperty = useCallback((formData: PropertyEditFormData) => {
    setState((prev) => {
      const newSchema = { ...prev.schema };

      if (prev.editingState.mode === 'add') {
        const currentPath = prev.editingState.path;

        // Get the target object to add the property to
        let parent: JsonSchema | JsonProperty;

        if (currentPath.length === 0) {
          parent = newSchema;
        } else {
          const targetObject = SchemaUtils.getPropertyAtPath(newSchema, currentPath);
          if (!targetObject || targetObject.type !== 'object') {
            return prev;
          }
          parent = targetObject;
        }

        if (parent.type !== 'object') return prev;

        // Create the property using utility function
        const newProperty = PropertyUtils.createFromFormData(formData);

        // Add to properties
        if (!parent.properties) parent.properties = {};
        parent.properties[formData.key] = newProperty;

        // Handle required field using utility function
        PropertyUtils.updateRequiredFields(parent, undefined, formData.key, formData.required);
      } else if (prev.editingState.mode === 'edit') {
        const fullPath = prev.editingState.path;
        const parentPath = fullPath.slice(0, -1);
        const currentKey = fullPath[fullPath.length - 1];

        // Special case for array items
        if (currentKey === 'items') {
          const arrayProperty = SchemaUtils.getPropertyAtPath(newSchema, parentPath);
          if (arrayProperty?.type === 'array') {
            arrayProperty.items = PropertyUtils.createFromFormData(formData);
          }
        } else {
          // Regular property editing
          const parentInfo = SchemaUtils.getParentAtPath(newSchema, fullPath);
          if (!parentInfo) return prev;

          const { parent, key } = parentInfo;
          if (parent.type !== 'object') return prev;

          const existingProperty = parent.properties?.[key];

          // Create updated property using utility function
          const newProperty = existingProperty
            ? PropertyUtils.updateExisting(existingProperty, formData)
            : PropertyUtils.createFromFormData(formData);

          if (!parent.properties) parent.properties = {};

          // Handle key changes and remove old key if necessary
          if (formData.key !== key) {
            delete parent.properties[key];
          }

          // Set new property
          parent.properties[formData.key] = newProperty;

          // Handle required field using utility function
          PropertyUtils.updateRequiredFields(parent, key, formData.key, formData.required);
        }
      }

      return {
        ...prev,
        schema: newSchema,
        editingState: { mode: 'none' }
      };
    });
  }, []);

  const deleteProperty = useCallback(
    (fullPath: string[]) => {
      updateSchema((schema) => {
        const newSchema = { ...schema };
        const parentInfo = SchemaUtils.getParentAtPath(newSchema, fullPath);

        if (!parentInfo) return schema;

        const { parent, key } = parentInfo;

        if (parent.type === 'object' && parent.properties) {
          delete parent.properties[key];

          // Remove from required array if present
          parent.required = parent.required.filter((req) => req !== key);
        }

        return newSchema;
      });
    },
    [updateSchema]
  );

  const toggleRequired = useCallback(
    (fullPath: string[], isRequired: boolean) => {
      updateSchema((schema) => {
        const newSchema = { ...schema };
        const parentInfo = SchemaUtils.getParentAtPath(newSchema, fullPath);

        if (!parentInfo) return schema;

        const { parent, key } = parentInfo;

        if (parent.type === 'object') {
          if (isRequired) {
            if (!parent.required.includes(key)) {
              parent.required.push(key);
            }
          } else {
            parent.required = parent.required.filter((req) => req !== key);
          }
        }

        return newSchema;
      });
    },
    [updateSchema]
  );

  // Reset state when initialSchema changes
  useEffect(() => {
    let schema = DEFAULT_JSON_SCHEMA;

    if (initialSchema) {
      try {
        const parsed = JSON.parse(initialSchema);
        if (parsed.type === 'object' && parsed.properties) {
          schema = parsed;
        }
      } catch {
        // Use default if parsing fails
      }
    }

    setState({
      schema,
      editingState: { mode: 'none' }
    });
  }, [initialSchema]);

  return {
    // State
    editingState: state.editingState,

    // Schema accessors (single source of truth)
    getSchema,
    getSchemaString,
    getPropertyAtSchemaPath,
    getExistingKeys,
    isPropertyRequired,

    // Actions
    startAddingProperty,
    startEditingProperty,
    cancelEditing,
    saveProperty,
    deleteProperty,
    toggleRequired
  };
};
